// The app-side half of the errors split.
//
// NOT A SHIM. `src/tauri/errors/**` moved into `@heidi/plugin-core` — history:
// `git log --follow -- packages/plugin-core/src/errors/index.ts` — but
// `translateDesktopError` did NOT, and it is the reason this module still has a
// body. It is the ONLY i18next consumer in the whole thirteen-file subtree and
// its sole non-relative import, and the programme owner ruled i18next stays out
// of the package (§0 ruling 4). So the pure half is published on the door and
// the translation is rebuilt here, on top of it.
//
// Its fan-in is 5 files / 9 sites — `src/tauri/{auth0/index,ipc}.ts`,
// `src/integrations/{genie,gdt}/api/constants.ts`,
// `src/hooks/desktop/ota-start-failure.ts` — and every one of them keeps
// importing `@/tauri/errors`. Repointing them at the door would not compile:
// the door does not publish this function and never will.
//
// THE TWO-FUNCTION REBUILD, and why one would not do. The pre-move body branched
// on `if (!ERROR_CODE_MAP[errorCode]) return message;` — it had to tell an
// UNMAPPED code from a mapped one, because for an unmapped code the raw backend
// message carries actionable detail the generic fallback copy does not.
// `resolveDesktopErrorKey` collapses both onto `desktop.errors.unknown`, so the
// door publishes `hasDesktopErrorMapping` alongside it and this function reads
// both. `errors.test.ts` holds the result against the pre-move behaviour over
// mapped / unmapped / absent / maps-to-fallback codes.
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { hasDesktopErrorMapping, resolveDesktopErrorKey } from '@heidi/plugin-core';
import { formatMessageOutsideReact } from '@heidi/runtime/i18n';
import i18next from 'i18next';
import type { MessageDescriptor } from 'react-intl';
import { desktopErrorContactItMessages, desktopErrorMessages } from './errors.messages';

// Re-exported because the pre-move `@/tauri/errors` barrel exported it, so the
// module's surface is unchanged in both directions.
export { resolveDesktopErrorKey };

// THE REACT-INTL LAYER, and why it sits in front rather than behind.
//
// The i18next catalog it falls through to (`src/locales/en/common.json`) is
// frozen against additions — `scripts/check-common-json-additions.ts` fails CI
// on a new key — so a desktop error code registered after the freeze can never
// reach `ERROR_CODE_MAP` with copy behind it. Without this layer it falls to
// `translateDesktopError`'s unmapped branch and the raw Rust English is shown
// verbatim by `src/tauri/ipc.ts`.
//
// Checked FIRST, not last, so that migrating a code out of the frozen catalog
// later is a one-line move: add it here and the i18next entry is dead rather
// than contested. Nothing is in both today, and
// `errors.rust-emitted-codes.test.ts` is what keeps the two honest.
//
// `RECORDING_START_TIMEOUT` deliberately has NO entry, though it is a recorder
// code with no i18next copy. It never reaches this function: it is produced
// only by `RecordingError::StartTimeout`, which reaches the frontend only as an
// `ipc_invoke` rejection from `start_recording`, and that call site passes the
// code in `suppressErrorCodes` because the start flow toasts its own copy
// (`startTimeoutMessages`) with more useful guidance. A descriptor here would
// be a second, divergent string extracted and translated into every locale for
// a toast no clinician ever sees. It is exempted in
// `errors.rust-emitted-codes.test.ts` instead, which is what keeps that
// reasoning checkable rather than remembered.
const REACT_INTL_ERROR_COPY: Readonly<Record<string, MessageDescriptor>> = {
  RECORDING_FAILED: desktopErrorMessages.recordingFailed,
};

const CONTACT_IT_COPY: Readonly<Record<string, MessageDescriptor>> =
  desktopErrorContactItMessages;

/**
 * The codes whose copy comes from {@link desktopErrorMessages} rather than the
 * frozen i18next catalog. Exported for `errors.rust-emitted-codes.test.ts`,
 * which has to tell the two layers apart: for an i18next-layer code it goes on
 * to assert the resolved key actually holds a string in `en/common.json`, and
 * that assertion would be wrong for a code that never reaches i18next at all.
 */
export const REACT_INTL_DESKTOP_ERROR_CODES: readonly string[] =
  Object.keys(REACT_INTL_ERROR_COPY);

/** Whether `errorCode` has copy on either layer. Read by the enforcement test. */
export function hasDesktopErrorCopy(errorCode?: string): boolean {
  if (!errorCode) return false;
  return errorCode in REACT_INTL_ERROR_COPY || hasDesktopErrorMapping(errorCode);
}

export function translateDesktopError(message: string, errorCode?: string): string {
  if (!errorCode) return message;
  const descriptor = REACT_INTL_ERROR_COPY[errorCode];
  if (descriptor) {
    // `formatMessageOutsideReact` already falls back to the English
    // `defaultMessage` before a provider mounts, so this cannot return the id
    // or an empty string the way the i18next branch below can.
    return formatMessageOutsideReact(descriptor);
  }
  // If the error code has no specific mapping, prefer the raw message
  // which often contains actionable details from the backend
  if (!hasDesktopErrorMapping(errorCode)) return message;
  const i18nKey = resolveDesktopErrorKey(errorCode);
  const contactItCopy = isHeidiSupportReachable ? undefined : CONTACT_IT_COPY[i18nKey];
  if (contactItCopy) return formatMessageOutsideReact(contactItCopy);
  const translated = i18next.t(i18nKey, { defaultValue: '' });
  if (!translated || translated === i18nKey) return message;
  return translated;
}
